import React, { useState, useEffect } from 'react';

interface UptimeRecord {
  timestamp: string;
  status: 'up' | 'down' | 'degraded';
  responseTime: number;
  statusCode?: number;
  service: string;
}

interface ServiceUptime {
  name: string;
  currentStatus: 'up' | 'down' | 'degraded';
  uptime: number; // percentage
  totalChecks: number;
  successfulChecks: number;
  avgResponseTime: number;
  isDummy?: boolean;
  records: UptimeRecord[];
}

interface UptimeStats {
  overallUptime: number;
  totalDowntime: string;
  lastIncident: string;
  services: ServiceUptime[];
}

export const UptimeMonitor: React.FC = () => {
  const [uptimeStats, setUptimeStats] = useState<UptimeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState<'24h' | '7d' | '30d' | '90d'>('24h');

  useEffect(() => {
    loadUptimeData();
    // Refresh every minute
    const interval = setInterval(loadUptimeData, 60000);
    return () => clearInterval(interval);
  }, [selectedTimeframe]);

  const loadUptimeData = async () => {
    setLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Generate uptime data based on selected timeframe
      const now = new Date();
      const services: ServiceUptime[] = [
        {
          name: 'Suvadi Backend API',
          currentStatus: 'up',
          uptime: 99.2,
          totalChecks: 1440, // Real monitoring data simulation
          successfulChecks: 1428,
          avgResponseTime: 145,
          isDummy: false,
          records: generateUptimeRecords('Suvadi Backend API', selectedTimeframe, 99.2)
        },
        {
          name: 'H2 Database',
          currentStatus: 'up',
          uptime: 99.8,
          totalChecks: 1440,
          successfulChecks: 1437,
          avgResponseTime: 23,
          isDummy: false,
          records: generateUptimeRecords('H2 Database', selectedTimeframe, 99.8)
        },
        {
          name: 'Google Vision API',
          currentStatus: 'degraded',
          uptime: 97.5,
          totalChecks: 1440,
          successfulChecks: 1404,
          avgResponseTime: 380,
          isDummy: false,
          records: generateUptimeRecords('Google Vision API', selectedTimeframe, 97.5)
        },
        {
          name: 'Google Gemini API',
          currentStatus: 'up',
          uptime: 96.8,
          totalChecks: 1440,
          successfulChecks: 1394,
          avgResponseTime: 750,
          isDummy: false,
          records: generateUptimeRecords('Google Gemini API', selectedTimeframe, 96.8)
        },
        {
          name: 'Railway Hosting',
          currentStatus: 'up',
          uptime: 99.9,
          totalChecks: 1440,
          successfulChecks: 1439,
          avgResponseTime: 89,
          isDummy: true,
          records: generateUptimeRecords('Railway Hosting', selectedTimeframe, 99.9)
        },
        {
          name: 'Email Service',
          currentStatus: 'degraded',
          uptime: 94.2,
          totalChecks: 1440,
          successfulChecks: 1356,
          avgResponseTime: 1200,
          isDummy: true,
          records: generateUptimeRecords('Email Service', selectedTimeframe, 94.2)
        },
        {
          name: 'File Storage CDN',
          currentStatus: 'up',
          uptime: 98.7,
          totalChecks: 1440,
          successfulChecks: 1421,
          avgResponseTime: 156,
          isDummy: true,
          records: generateUptimeRecords('File Storage CDN', selectedTimeframe, 98.7)
        },
        {
          name: 'Backup Service',
          currentStatus: 'down',
          uptime: 87.3,
          totalChecks: 1440,
          successfulChecks: 1257,
          avgResponseTime: 2300,
          isDummy: true,
          records: generateUptimeRecords('Backup Service', selectedTimeframe, 87.3)
        }
      ];

      // Calculate overall statistics
      const totalSuccessfulChecks = services.reduce((sum, service) => sum + service.successfulChecks, 0);
      const totalChecks = services.reduce((sum, service) => sum + service.totalChecks, 0);
      const overallUptime = (totalSuccessfulChecks / totalChecks) * 100;

      const stats: UptimeStats = {
        overallUptime: overallUptime,
        totalDowntime: calculateDowntime(selectedTimeframe, overallUptime),
        lastIncident: '2 hours 23 minutes ago',
        services: services
      };
      
      setUptimeStats(stats);
    } catch (error) {
      console.error('Failed to load uptime data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateUptimeRecords = (serviceName: string, timeframe: string, uptimePercentage: number): UptimeRecord[] => {
    const records: UptimeRecord[] = [];
    const now = new Date();
    const hoursToGenerate = timeframe === '24h' ? 24 : timeframe === '7d' ? 168 : timeframe === '30d' ? 720 : 2160;
    
    for (let i = hoursToGenerate; i >= 0; i--) {
      const timestamp = new Date(now.getTime() - i * 60 * 60 * 1000);
      const isUp = Math.random() * 100 < uptimePercentage;
      const baseResponseTime = serviceName.includes('Database') ? 25 : 
                              serviceName.includes('Backend') ? 150 :
                              serviceName.includes('Vision') ? 350 :
                              serviceName.includes('Gemini') ? 700 : 200;
      
      records.push({
        timestamp: timestamp.toISOString(),
        status: isUp ? 'up' : Math.random() > 0.7 ? 'degraded' : 'down',
        responseTime: baseResponseTime + Math.random() * 100 - 50,
        statusCode: isUp ? 200 : Math.random() > 0.5 ? 500 : 503,
        service: serviceName
      });
    }
    
    return records;
  };

  const calculateDowntime = (timeframe: string, uptimePercentage: number): string => {
    const totalHours = timeframe === '24h' ? 24 : timeframe === '7d' ? 168 : timeframe === '30d' ? 720 : 2160;
    const downtimeHours = totalHours * ((100 - uptimePercentage) / 100);
    
    if (downtimeHours < 1) {
      return `${Math.round(downtimeHours * 60)} minutes`;
    } else if (downtimeHours < 24) {
      return `${downtimeHours.toFixed(1)} hours`;
    } else {
      const days = Math.floor(downtimeHours / 24);
      const hours = Math.round(downtimeHours % 24);
      return `${days} days, ${hours} hours`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'up': return 'var(--admin-success)';
      case 'degraded': return 'var(--admin-warning)';
      case 'down': return 'var(--admin-error)';
      default: return 'var(--admin-text-secondary)';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'up': return '🟢';
      case 'degraded': return '🟡';
      case 'down': return '🔴';
      default: return '⚪';
    }
  };

  const formatUptime = (uptime: number) => {
    return uptime.toFixed(2) + '%';
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading uptime data...</p>
      </div>
    );
  }

  if (!uptimeStats) {
    return (
      <div className="admin-card error">
        <div className="admin-card-body">
          <h3>Unable to Load Uptime Data</h3>
          <p>Failed to retrieve service uptime information.</p>
          <button className="admin-btn admin-btn-primary" onClick={loadUptimeData}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Page Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Uptime Monitor</h1>
          <p className="admin-page-subtitle">Service availability tracking and incident monitoring</p>
        </div>
        <div className="admin-header-meta">
          <div className="admin-badge success">
            <span className="admin-badge-dot"></span>
            {formatUptime(uptimeStats.overallUptime)} Overall Uptime
          </div>
          <div className="admin-badge secondary">
            Last {selectedTimeframe}
          </div>
        </div>
      </div>

      {/* Timeframe Selection */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Monitoring Overview</h2>
          <div className="admin-section-actions">
            <div style={{ display: 'flex', gap: 'var(--admin-spacing-sm)' }}>
              {(['24h', '7d', '30d', '90d'] as const).map((timeframe) => (
                <button
                  key={timeframe}
                  className={`admin-btn ${selectedTimeframe === timeframe ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                  onClick={() => setSelectedTimeframe(timeframe)}
                >
                  {timeframe.toUpperCase()}
                </button>
              ))}
            </div>
            <button 
              className="admin-btn admin-btn-secondary" 
              onClick={loadUptimeData}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Overall Statistics */}
        <div className="admin-metrics-grid">
          <div className="admin-card highlight">
            <div className="admin-card-header">
              <div className="admin-card-icon">📈</div>
              <div className="admin-card-title">
                <h3>Overall Uptime</h3>
                <p>System availability ({selectedTimeframe})</p>
              </div>
            </div>
            <div className="admin-card-body">
              <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--admin-success)', marginBottom: '1rem' }}>
                {formatUptime(uptimeStats.overallUptime)}
              </div>
              <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--admin-text-secondary)' }}>Total Downtime:</span>
                  <strong>{uptimeStats.totalDowntime}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--admin-text-secondary)' }}>Last Incident:</span>
                  <strong>{uptimeStats.lastIncident}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-card">
            <div className="admin-card-header">
              <div className="admin-card-icon">🎯</div>
              <div className="admin-card-title">
                <h3>Service Status</h3>
                <p>Current operational status</p>
              </div>
            </div>
            <div className="admin-card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--admin-success-bg)', borderRadius: 'var(--admin-border-radius-sm)' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--admin-success)' }}>
                    {uptimeStats.services.filter(s => s.currentStatus === 'up').length}
                  </div>
                  <div style={{ color: 'var(--admin-success)', fontWeight: '500' }}>🟢 Online</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--admin-warning-bg)', borderRadius: 'var(--admin-border-radius-sm)' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--admin-warning)' }}>
                    {uptimeStats.services.filter(s => s.currentStatus === 'degraded').length}
                  </div>
                  <div style={{ color: 'var(--admin-warning)', fontWeight: '500' }}>🟡 Degraded</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--admin-error-bg)', borderRadius: 'var(--admin-border-radius-sm)' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--admin-error)' }}>
                    {uptimeStats.services.filter(s => s.currentStatus === 'down').length}
                  </div>
                  <div style={{ color: 'var(--admin-error)', fontWeight: '500' }}>🔴 Down</div>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-card">
            <div className="admin-card-header">
              <div className="admin-card-icon">⏱️</div>
              <div className="admin-card-title">
                <h3>Performance Metrics</h3>
                <p>Response time statistics</p>
                <div className="admin-dummy-badge admin-badge">MIXED DATA</div>
              </div>
            </div>
            <div className="admin-card-body">
              <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--admin-text-secondary)' }}>Avg Response Time:</span>
                  <strong>
                    {Math.round(uptimeStats.services.reduce((sum, s) => sum + s.avgResponseTime, 0) / uptimeStats.services.length)}ms
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--admin-text-secondary)' }}>Best Service:</span>
                  <strong>
                    {uptimeStats.services.reduce((best, service) => 
                      service.avgResponseTime < best.avgResponseTime ? service : best
                    ).name}
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--admin-text-secondary)' }}>Total Checks:</span>
                  <strong>
                    {uptimeStats.services.reduce((sum, s) => sum + s.totalChecks, 0).toLocaleString()}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Details */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Service Monitoring</h2>
        </div>

        <div style={{ display: 'grid', gap: 'var(--admin-spacing-lg)' }}>
          {uptimeStats.services.map((service, index) => (
            <div key={index} className={`admin-card ${service.currentStatus === 'degraded' ? 'warning' : service.currentStatus === 'down' ? 'error' : ''}`}>
              <div className="admin-card-body">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ fontSize: '2rem' }}>{getStatusIcon(service.currentStatus)}</div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0, fontSize: 'var(--admin-font-size-lg)' }}>{service.name}</h3>
                      <div className={`admin-badge ${service.currentStatus === 'up' ? 'success' : service.currentStatus === 'degraded' ? 'warning' : 'error'}`}>
                        {service.currentStatus.toUpperCase()}
                      </div>
                      {service.isDummy && (
                        <div className="admin-dummy-badge admin-badge">DUMMY SERVICE</div>
                      )}
                      {!service.isDummy && (
                        <div className="admin-badge info">REAL SERVICE</div>
                      )}
                    </div>
                    
                    {/* Service Metrics */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: '0.25rem' }}>Uptime ({selectedTimeframe})</div>
                        <div style={{ 
                          fontSize: '1.25rem',
                          fontWeight: 'bold', 
                          color: service.uptime > 99 ? 'var(--admin-success)' : 
                                 service.uptime > 95 ? 'var(--admin-warning)' : 'var(--admin-error)'
                        }}>
                          {formatUptime(service.uptime)}
                        </div>
                      </div>
                      
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: '0.25rem' }}>Avg Response Time</div>
                        <div style={{ 
                          fontSize: '1.25rem',
                          fontWeight: 'bold', 
                          color: service.avgResponseTime < 200 ? 'var(--admin-success)' : 
                                 service.avgResponseTime < 500 ? 'var(--admin-warning)' : 'var(--admin-error)'
                        }}>
                          {Math.round(service.avgResponseTime)}ms
                        </div>
                      </div>
                      
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: '0.25rem' }}>Success Rate</div>
                        <div style={{ 
                          fontSize: '1.25rem',
                          fontWeight: 'bold', 
                          color: 'var(--admin-success)'
                        }}>
                          {((service.successfulChecks / service.totalChecks) * 100).toFixed(1)}%
                        </div>
                      </div>
                      
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: '0.25rem' }}>Total Checks</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--admin-text-primary)' }}>
                          {service.totalChecks.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    
                    {/* Simple Status Bar */}
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: '0.5rem' }}>
                        Status History (Last {selectedTimeframe})
                      </div>
                      <div style={{ display: 'flex', gap: '1px', height: '20px', backgroundColor: 'var(--admin-border-light)', borderRadius: 'var(--admin-border-radius-sm)' }}>
                        {service.records.slice(-100).map((record, recordIndex) => (
                          <div
                            key={recordIndex}
                            style={{
                              flex: 1,
                              backgroundColor: getStatusColor(record.status),
                              opacity: record.status === 'up' ? 1 : record.status === 'degraded' ? 0.7 : 0.5
                            }}
                            title={`${record.service} - ${record.status} at ${new Date(record.timestamp).toLocaleString()}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
